import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "../../../database/firebase";
import {
  ArrowLeft,
  ShoppingCart,
  Calendar,
  DollarSign,
  Filter,
  Search,
  Download,
} from "lucide-react";

const RechargeHistory = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [recharges, setRecharges] = useState([]);
  const [filteredRecharges, setFilteredRecharges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  // Stats
  const [stats, setStats] = useState({
    totalRecharges: 0,
    totalSpent: 0,
    currentBalance: 0,
  });

  useEffect(() => {
    if (user) {
      fetchRecharges();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [recharges, searchTerm, filterType, dateFilter]);

  const fetchRecharges = async () => {
    try {
      setIsLoading(true);

      // Fetch from orders collection
      const ordersRef = collection(db, "Orders");
      const q = query(
        ordersRef,
        where("userId", "==", user.uid),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(q);
      const rechargeData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        rechargeData.push({
          id: doc.id,
          ...data,
          // Map new structure to old structure expected by UI
          billNo: data.orderId || data.billNo,
          totalAmount: data.summary?.grandTotalAmount || data.totalAmount || 0,
          transactionType: data.transaction?.type || data.transactionType,
          paymentMethod: data.transaction?.paymentMethod || data.paymentMethod,
          products: data.items || data.products || [],
          createdAt: data.timestamp || data.createdAt,
          shakeQuantityChange: data.summary?.shakeBalanceChange,
          // These might not be available in new structure, but keeping for backward compatibility
          previousShakeQuantity: data.previousShakeQuantity,
          newShakeQuantity: data.newShakeQuantity
        });
      });

      setRecharges(rechargeData);

      // Calculate stats
      const totalSpent = rechargeData.reduce(
        (sum, r) => sum + (r.totalAmount || 0),
        0
      );

      setStats({
        totalRecharges: rechargeData.length,
        totalSpent: totalSpent,
        currentBalance: user.shakeQuantity || 0,
      });
    } catch (error) {
      console.error("Error fetching recharges:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...recharges];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (r) =>
          r.billNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.transactionType?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Transaction type filter
    if (filterType !== "all") {
      filtered = filtered.filter((r) => r.transactionType === filterType);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      filtered = filtered.filter((r) => {
        const rechargeDate = new Date(r.createdAt);
        switch (dateFilter) {
          case "today":
            return rechargeDate.toDateString() === now.toDateString();
          case "week":
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return rechargeDate >= weekAgo;
          case "month":
            return (
              rechargeDate.getMonth() === now.getMonth() &&
              rechargeDate.getFullYear() === now.getFullYear()
            );
          default:
            return true;
        }
      });
    }

    setFilteredRecharges(filtered);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTransactionTypes = () => {
    const types = new Set(recharges.map((r) => r.transactionType));
    return Array.from(types);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="w-full max-w-4xl mx-auto bg-white shadow-sm">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/profile")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">
                Recharge History
              </h1>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
            <div className="text-sm text-blue-600 font-medium mb-1">
              Total Recharges
            </div>
            <div className="text-2xl font-bold text-blue-900">
              {stats.totalRecharges}
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
            <div className="text-sm text-green-600 font-medium mb-1">
              Total Spent
            </div>
            <div className="text-2xl font-bold text-green-900">
              ₹{stats.totalSpent.toFixed(2)}
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
            <div className="text-sm text-purple-600 font-medium mb-1">
              Current Balance
            </div>
            <div className="text-2xl font-bold text-purple-900">
              {stats.currentBalance} Shakes
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-5 pb-4 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by bill number or type..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
            >
              <option value="all">All Types</option>
              {getTransactionTypes().map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>

        {/* Recharge List */}
        <div className="px-5 pb-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading recharges...</div>
            </div>
          ) : filteredRecharges.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No recharges found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRecharges.map((recharge) => (
                <div
                  key={recharge.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="text-sm font-semibold text-red-600">
                        Bill#: {recharge.billNo || recharge.id?.slice(0, 8)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(recharge.createdAt)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        ₹{recharge.totalAmount?.toFixed(2) || "0.00"}
                      </div>
                      <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded mt-1">
                        {recharge.transactionType}
                      </div>
                    </div>
                  </div>

                  {/* Products */}
                  {recharge.products && recharge.products.length > 0 && (
                    <div className="border-t border-gray-100 pt-3 mt-3">
                      <div className="text-xs text-gray-500 mb-2">Items:</div>
                      {recharge.products.map((product, idx) => (
                        <div
                          key={idx}
                          className="text-sm text-gray-700 flex justify-between mb-1"
                        >
                          <span>
                            {product.productName || product.name} ×{" "}
                            {product.quantity}
                          </span>
                          <span className="text-gray-500">
                            ₹
                            {(
                              (product.price || product.mrp || 0) *
                              product.quantity
                            ).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Shake Quantity Change */}
                  {recharge.shakeQuantityChange && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-600">
                        Shake Balance: {recharge.previousShakeQuantity || 0} →{" "}
                        <span className="font-semibold text-green-600">
                          {recharge.newShakeQuantity || 0}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Payment Method */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <DollarSign className="w-3 h-3" />
                      <span className="uppercase">
                        {recharge.paymentMethod || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RechargeHistory;
